import { useEffect, useState } from "react";
import {
  fetchAcademyTestimonials,
  createAcademyTestimonial,
  deleteAcademyTestimonial,
} from "@/api/academies";

export default function AcademyTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadTestimonials = async () => {
    setLoading(true);
    try {
      const data = await fetchAcademyTestimonials();
      setTestimonials(data || []);
    } catch (e) {
      console.error(e);
      setError("فشل تحميل آراء المستخدمين");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTestimonials();
  }, []);

  const handleCreate = async () => {
    if (!name.trim() || !content.trim()) {
      setError("الرجاء ملء جميع الحقول المطلوبة");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      await createAcademyTestimonial({
        name: name.trim(),
        content: content.trim(),
      });

      setName("");
      setContent("");
      setSuccess("تم إضافة الرأي بنجاح");
      
      // إخفاء النموذج بعد الإضافة الناجحة
      setTimeout(() => {
        setIsFormVisible(false);
        setSuccess("");
      }, 2000);
      
      loadTestimonials();
    } catch (e) {
      console.error(e);
      setError("فشل إضافة الرأي. يرجى المحاولة مرة أخرى.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الرأي؟")) return;

    try {
      await deleteAcademyTestimonial(id);
      setSuccess("تم حذف الرأي بنجاح");
      loadTestimonials();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      console.error(e);
      setError("فشل حذف الرأي");
    }
  };

  const resetForm = () => {
    setName("");
    setContent("");
    setError("");
    setIsFormVisible(false);
  };

  // تنسيقات CSS
  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "25px 20px",
      minHeight: "calc(100vh - 160px)"
    },
    header: {
      marginBottom: "30px",
      paddingBottom: "20px",
      borderBottom: "2px solid #e9ecef"
    },
    title: {
      margin: "0 0 10px 0",
      color: "#182848",
      fontSize: "28px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    subtitle: {
      margin: "0 0 20px 0",
      color: "#6c757d",
      fontSize: "16px"
    },
    statsCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
      display: "flex",
      alignItems: "center",
      gap: "15px",
      marginBottom: "30px"
    },
    statIcon: {
      width: "50px",
      height: "50px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: "0",
      backgroundColor: "rgba(75, 108, 183, 0.1)"
    },
    statContent: {
      flex: "1"
    },
    statTitle: {
      margin: "0 0 5px 0",
      color: "#6c757d",
      fontSize: "14px",
      fontWeight: "500"
    },
    statValue: {
      margin: "0",
      color: "#182848",
      fontSize: "24px",
      fontWeight: "700"
    },
    actionBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "25px",
      flexWrap: "wrap",
      gap: "15px"
    },
    toggleButton: {
      padding: "12px 25px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontFamily: "inherit"
    },
    testimonialsCount: {
      color: "#182848",
      fontSize: "16px",
      fontWeight: "600",
      backgroundColor: "rgba(75, 108, 183, 0.1)",
      padding: "8px 16px",
      borderRadius: "20px"
    },
    formContainer: {
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
      padding: "25px",
      marginBottom: "30px",
      display: isFormVisible ? "block" : "none",
      animation: "slideDown 0.3s ease-out"
    },
    formTitle: {
      margin: "0 0 25px 0",
      color: "#182848",
      fontSize: "20px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      paddingBottom: "15px",
      borderBottom: "1px solid #e9ecef"
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px"
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    },
    label: {
      color: "#495057",
      fontWeight: "500",
      fontSize: "15px",
      display: "flex",
      alignItems: "center",
      gap: "5px"
    },
    required: {
      color: "#dc3545"
    },
    input: {
      padding: "14px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "16px",
      transition: "all 0.3s",
      backgroundColor: "#f8f9fa",
      boxSizing: "border-box",
      fontFamily: "inherit",
      width: "100%"
    },
    textarea: {
      padding: "14px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "16px",
      transition: "all 0.3s",
      backgroundColor: "#f8f9fa",
      boxSizing: "border-box",
      fontFamily: "inherit",
      width: "100%",
      minHeight: "120px",
      resize: "vertical"
    },
    formButtons: {
      display: "flex",
      gap: "15px",
      marginTop: "10px"
    },
    submitButton: {
      padding: "14px 25px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    cancelButton: {
      padding: "14px 25px",
      backgroundColor: "#f8f9fa",
      color: "#495057",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    buttonDisabled: {
      opacity: "0.7",
      cursor: "not-allowed"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255, 255, 255, 0.3)",
      borderRadius: "50%",
      borderTopColor: "white",
      animation: "spin 1s ease-in-out infinite"
    },
    successMessage: {
      backgroundColor: "#d4edda",
      border: "1px solid #c3e6cb",
      color: "#155724",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    errorMessage: {
      backgroundColor: "#f8d7da",
      border: "1px solid #f5c6cb",
      color: "#721c24",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    icon: {
      width: "20px",
      height: "20px",
      flexShrink: "0"
    },
    testimonialsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      gap: "25px",
      marginTop: "10px"
    },
    testimonialCard: {
      backgroundColor: "white",
      borderRadius: "15px",
      overflow: "hidden",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.08)",
      transition: "all 0.3s ease",
      position: "relative",
      border: "1px solid #e9ecef",
      display: "flex",
      flexDirection: "column"
    },
    testimonialContent: {
      padding: "25px",
      flex: "1",
      display: "flex",
      flexDirection: "column"
    },
    quoteIcon: {
      fontSize: "36px",
      color: "#4b6cb7",
      opacity: "0.3",
      marginBottom: "15px"
    },
    testimonialText: {
      margin: "0 0 20px 0",
      color: "#495057",
      fontSize: "16px",
      lineHeight: "1.7",
      fontStyle: "italic",
      flex: "1"
    },
    testimonialFooter: {
      padding: "20px 25px",
      backgroundColor: "#f8f9fa",
      borderTop: "1px solid #e9ecef",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    authorInfo: {
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    authorAvatar: {
      width: "45px",
      height: "45px",
      borderRadius: "50%",
      backgroundColor: "#4b6cb7",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontWeight: "600",
      fontSize: "18px",
      flexShrink: "0"
    },
    authorName: {
      margin: "0",
      color: "#182848",
      fontSize: "16px",
      fontWeight: "600"
    },
    deleteButton: {
      padding: "8px 16px",
      backgroundColor: "rgba(220, 53, 69, 0.1)",
      color: "#dc3545",
      border: "1px solid rgba(220, 53, 69, 0.3)",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s"
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      gap: "20px"
    },
    spinnerLarge: {
      width: "50px",
      height: "50px",
      border: "4px solid #f3f3f3",
      borderTop: "4px solid #4b6cb7",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    },
    loadingText: {
      color: "#6c757d",
      fontSize: "18px",
      margin: "0"
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
      gridColumn: "1 / -1"
    },
    emptyIcon: {
      fontSize: "64px",
      color: "#adb5bd",
      marginBottom: "20px",
      opacity: "0.5"
    },
    emptyTitle: {
      margin: "0 0 10px 0",
      color: "#495057",
      fontSize: "24px",
      fontWeight: "600"
    },
    emptyText: {
      margin: "0 0 25px 0",
      color: "#6c757d",
      fontSize: "16px"
    }
  };

  // أيقونات SVG
  const TestimonialIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#4b6cb7", width: "28px", height: "28px" }}>
      <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H7.5z" clipRule="evenodd" />
    </svg>
  );

  const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#155724" }}>
      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
    </svg>
  );

  const ErrorIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#721c24" }}>
      <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
    </svg>
  );

  const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "18px", height: "18px" }}>
      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 9a.75.75 0 00-1.5 0v2.25H9a.75.75 0 000 1.5h2.25V15a.75.75 0 001.5 0v-2.25H15a.75.75 0 000-1.5h-2.25V9z" clipRule="evenodd" />
    </svg>
  );

  // دالة للحصول على الأحرف الأولى من الاسم لعرضها في الصورة الرمزية
  const getInitials = (name) => {
    if (!name) return "م";
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // تأثيرات hover
  const handleInputFocus = (e) => {
    e.target.style.outline = "none";
    e.target.style.borderColor = "#4b6cb7";
    e.target.style.boxShadow = "0 0 0 3px rgba(75, 108, 183, 0.1)";
    e.target.style.backgroundColor = "white";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#e1e5eb";
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = "#f8f9fa";
  };

  const handleButtonHover = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 7px 14px rgba(75, 108, 183, 0.2)";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.2)";
      e.currentTarget.style.transform = "translateY(-1px)";
    } else if (type === "toggle") {
      e.currentTarget.style.transform = "translateY(-2px)";
    }
  };

  const handleButtonLeave = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    } else if (type === "toggle") {
      e.currentTarget.style.transform = "translateY(0)";
    }
  };

  const handleCardHover = (e) => {
    e.currentTarget.style.transform = "translateY(-8px)";
    e.currentTarget.style.boxShadow = "0 15px 30px rgba(0, 0, 0, 0.15)";
    e.currentTarget.style.borderColor = "#4b6cb7";
  };

  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "0 5px 15px rgba(0, 0, 0, 0.08)";
    e.currentTarget.style.borderColor = "#e9ecef";
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinnerLarge}></div>
        <p style={styles.loadingText}>جارٍ تحميل آراء المستخدمين...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* رأس الصفحة */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <TestimonialIcon />
          آراء المستخدمين (Testimonials)
        </h1>
        <p style={styles.subtitle}>إدارة شهادات وتوصيات المستخدمين للأكاديمية</p>
      </div>

      {/* إحصائيات سريعة */}
      <div style={styles.statsCard}>
        <div style={styles.statIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4b6cb7" style={{ width: "24px", height: "24px" }}>
            <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H7.5z" clipRule="evenodd" />
          </svg>
        </div>
        <div style={styles.statContent}>
          <h3 style={styles.statTitle}>إجمالي آراء المستخدمين</h3>
          <p style={styles.statValue}>{testimonials.length}</p>
        </div>
      </div>

      {/* شريط الإجراءات */}
      <div style={styles.actionBar}>
        <button 
          style={styles.toggleButton}
          onClick={() => setIsFormVisible(!isFormVisible)}
          onMouseEnter={(e) => handleButtonHover(e, "toggle")}
          onMouseLeave={(e) => handleButtonLeave(e, "toggle")}
        >
          <PlusIcon />
          {isFormVisible ? "إخفاء نموذج الإضافة" : "إضافة رأي جديد"}
        </button>
        
        <div style={styles.testimonialsCount}>
          {testimonials.length} رأي{testimonials.length !== 1 ? 'اً' : ''}
        </div>
      </div>

      {/* نموذج إضافة رأي */}
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {isFormVisible ? "إضافة رأي جديد" : ""}
        </h2>
        
        {isFormVisible && (
          <form onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                اسم الشخص
                <span style={styles.required}>*</span>
              </label>
              <input
                placeholder="أدخل اسم صاحب الرأي"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                نص الرأي
                <span style={styles.required}>*</span>
              </label>
              <textarea
                placeholder="أدخل نص الرأي أو التوصية"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                style={styles.textarea}
                rows="5"
              />
              <small style={{ color: "#6c757d", fontSize: "13px" }}>
                يمكن أن يكون هذا رأي، توصية، أو شهادة من مستخدم
              </small>
            </div>

            <div style={styles.formButtons}>
              <button 
                type="submit" 
                style={{
                  ...styles.submitButton,
                  ...(isSubmitting ? styles.buttonDisabled : {})
                }}
                disabled={isSubmitting}
                onMouseEnter={(e) => handleButtonHover(e, "primary")}
                onMouseLeave={(e) => handleButtonLeave(e, "primary")}
              >
                {isSubmitting ? (
                  <>
                    <div style={styles.spinner}></div>
                    جاري الإضافة...
                  </>
                ) : (
                  "إضافة الرأي"
                )}
              </button>

              <button 
                type="button" 
                onClick={resetForm}
                style={styles.cancelButton}
                onMouseEnter={(e) => handleButtonHover(e, "secondary")}
                onMouseLeave={(e) => handleButtonLeave(e, "secondary")}
              >
                إلغاء
              </button>
            </div>

            {/* رسائل النجاح والخطأ */}
            {success && (
              <div style={styles.successMessage}>
                <SuccessIcon />
                {success}
              </div>
            )}

            {error && (
              <div style={styles.errorMessage}>
                <ErrorIcon />
                {error}
              </div>
            )}
          </form>
        )}
      </div>

      {/* شبكة آراء المستخدمين */}
      <div style={styles.testimonialsGrid}>
        {testimonials.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>💬</div>
            <h3 style={styles.emptyTitle}>لا توجد آراء مستخدمين</h3>
            <p style={styles.emptyText}>
              قم بإضافة أول رأي أو شهادة لتظهر هنا
            </p>
            <button 
              style={styles.toggleButton}
              onClick={() => setIsFormVisible(true)}
              onMouseEnter={(e) => handleButtonHover(e, "toggle")}
              onMouseLeave={(e) => handleButtonLeave(e, "toggle")}
            >
              <PlusIcon />
              إضافة أول رأي
            </button>
          </div>
        ) : (
          testimonials.map((testimonial) => (
            <div 
              key={testimonial.id} 
              style={styles.testimonialCard}
              onMouseEnter={handleCardHover}
              onMouseLeave={handleCardLeave}
            >
              <div style={styles.testimonialContent}>
                <div style={styles.quoteIcon}>❝</div>
                <p style={styles.testimonialText}>{testimonial.content}</p>
              </div>
              
              <div style={styles.testimonialFooter}>
                <div style={styles.authorInfo}>
                  <div style={styles.authorAvatar}>
                    {getInitials(testimonial.name)}
                  </div>
                  <div>
                    <h4 style={styles.authorName}>{testimonial.name}</h4>
                  </div>
                </div>
                
                <button 
                  onClick={() => handleDelete(testimonial.id)}
                  style={styles.deleteButton}
                  onMouseEnter={(e) => handleButtonHover(e, "delete")}
                  onMouseLeave={(e) => handleButtonLeave(e, "delete")}
                >
                  حذف الرأي
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* إضافة الأنيميشن */}
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 800px;
          }
        }
        
        .testimonial-card {
          animation: fadeIn 0.3s ease-out;
        }
        
        @media (max-width: 768px) {
          .testimonials-grid {
            grid-template-columns: 1fr !important;
          }
          
          .form-buttons {
            flex-direction: column !important;
          }
          
          .action-bar {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .toggle-button, .testimonials-count {
            width: 100% !important;
            text-align: center !important;
          }
          
          .author-info {
            flex-direction: column !important;
            text-align: center !important;
            gap: 8px !important;
          }
          
          .testimonial-footer {
            flex-direction: column !important;
            gap: 15px !important;
            text-align: center !important;
          }
        }
      `}</style>
    </div>
  );
}